import { type ColumnDef, createColumnHelper } from "@tanstack/react-table";
import { useQuery } from "convex/react";
import { useMemo } from "react";

import {
  locationSalesGetSalesProductMix,
  type ProductMixEntry,
} from "~/api-refs";
import { LoadingPanel } from "~/components/shared/loading-panel";
import { toConvexTableId } from "~/lib/convex-table-id";

import { SalesDataTable } from "../../sales-data-table";

type ExtendedItem = ProductMixEntry;

interface ProductMixViewProps {
  locationId: string;
  dateRange: {
    from: Date;
    to: Date;
  };
  tableId: string;
}

export const ProductMixView = ({
  locationId,
  dateRange,
  tableId,
}: ProductMixViewProps) => {
  const data =
    useQuery(locationSalesGetSalesProductMix, {
      dateRange: {
        from: dateRange.from.getTime(),
        to: dateRange.to.getTime(),
      },
      locationId: toConvexTableId("locations", locationId),
    }) ?? [];
  const isLoading = data === undefined;

  const columnHelper = createColumnHelper<ExtendedItem>();

  const columns = useMemo(
    () =>
      [
        columnHelper.accessor("posDevice", {
          header: "POS Device",
          cell: ({ getValue }) => getValue()?.name ?? "N/A",
          filterFn: ({ original }, _, filterValue: string[]) =>
            original.posDevice
              ? filterValue.includes(original.posDevice.id)
              : false,
          meta: {
            getCellForExport: (d: ExtendedItem["posDevice"]) =>
              d?.name ?? "N/A",
            faceting: {
              title: "POS Device",
              getOption: (e: ExtendedItem["posDevice"]) => ({
                value: e?.id ?? "",
                label: e?.name ?? e?.id ?? "N/A",
              }),
            },
          },
        }),
        columnHelper.accessor("orderType", {
          header: "Order Type",
          meta: {
            faceting: {
              title: "Order Type",
              getOption: (o: ExtendedItem["orderType"]) => ({
                value: o,
                label: o,
              }),
            },
          },
        }),
        columnHelper.accessor("menu", {
          header: "Menu",
          cell: ({ getValue }) => getValue().name,
          filterFn: ({ original }, _, filterValue: string[]) =>
            filterValue.includes(original.menu.id),
          meta: {
            getCellForExport: (d: ExtendedItem["menu"]) => d.name,
            faceting: {
              title: "Menu",
              getOption: (e: ExtendedItem["menu"]) => ({
                value: e.id,
                label: e.name,
              }),
            },
          },
        }),
        columnHelper.accessor("category", {
          header: "Category",
          cell: ({ getValue }) => getValue().name,
          filterFn: ({ original }, _, filterValue: string[]) =>
            filterValue.includes(original.category.id),
          meta: {
            getCellForExport: (d: ExtendedItem["category"]) => d.name,
            faceting: {
              title: "Category",
              getOption: (e: ExtendedItem["category"]) => ({
                value: e.id,
                label: e.name,
              }),
            },
          },
        }),
        columnHelper.accessor("item", {
          header: "Item",
          cell: ({ getValue }) => getValue().name,
          filterFn: ({ original }, _, filterValue: string[]) =>
            filterValue.includes(original.item.id),
          meta: {
            getCellForExport: (d: ExtendedItem["item"]) => d.name,
            faceting: {
              title: "Item",
              getOption: (e: ExtendedItem["item"]) => ({
                value: e.id,
                label: e.name,
              }),
            },
          },
        }),
        columnHelper.accessor("itemsSold", {
          header: "Items Sold",
          cell: ({ getValue }) => getValue()?.toLocaleString("en-US"),
          meta: {
            getCellForExport: (d: number) => d,
          },
        }),
        columnHelper.accessor("amountSold", {
          header: "Amount Sold",
          cell: ({ getValue }) =>
            getValue()?.toLocaleString("en-US", {
              style: "currency",
              currency: "USD",
            }),
          meta: {
            getCellForExport: (d: number) => d,
          },
        }),
        columnHelper.accessor("amountRefunded", {
          header: "Amount Refunded",
          cell: ({ getValue }) =>
            getValue()?.toLocaleString("en-US", {
              style: "currency",
              currency: "USD",
            }),
          meta: {
            getCellForExport: (d: number) => d,
          },
        }),
        columnHelper.display({
          id: "totalCollected",
          header: "Total Collected",
          cell: ({ row }) => {
            const total = row.original.amountSold - row.original.amountRefunded;
            return total?.toLocaleString("en-US", {
              style: "currency",
              currency: "USD",
            });
          },
          meta: {
            getCellForExport: (value: unknown) => {
              // For display columns, parse the formatted currency string back to number
              const stringValue =
                typeof value === "string" || typeof value === "number"
                  ? String(value)
                  : "";
              return (
                Number.parseFloat(stringValue.replace(/[^0-9.-]+/g, "")) || 0
              );
            },
          },
        }),
        columnHelper.accessor("shareOfMenuSales", {
          header: "% of Menu Sales",
          cell: ({ getValue }) =>
            `${(getValue() * 100).toLocaleString("en-US")}%`,
          meta: {
            getCellForExport: (d: number) => d,
          },
        }),
      ] as ColumnDef<ExtendedItem>[],
    [columnHelper]
  );

  if (isLoading) {
    return <LoadingPanel message="Loading product mix data..." />;
  }

  return <SalesDataTable columns={columns} data={data} id={tableId} />;
};
